import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Resource } from './types';
import FileIcon from './FileIcon.tsx';
import { getFileExtension, getTypeColor, extractResourceTitle } from './utils.ts';

interface ResourceCardProps {
  resource: Resource;
  onOpenFile: (url: string) => void;
}

const ResourceCard: React.FC<ResourceCardProps> = ({ resource, onOpenFile }) => {
  const typeColor = getTypeColor(resource.type);
  
  const styles = {
    resourceCard: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s, transform 0.2s'
    },
    cardContent: {
      padding: '24px'
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: '16px'
    },
    cardInfo: {
      marginLeft: '16px',
      flex: 1
    },
    cardTitle: {
      fontSize: '1.25rem',
      fontWeight: '500',
      color: '#1f2937',
      marginBottom: '8px',
      lineHeight: '1.4'
    },
    cardBadges: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '8px',
      marginBottom: '8px'
    },
    cardSeries: {
      fontSize: '1rem',
      color: '#6b7280',
      textAlign: 'start',
    },
    cardActions: {
      padding: '0 24px 24px'
    },
    openButton: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px 16px',
      backgroundColor: '#83FFE7',
      color: '#545454',
      fontSize: '1rem',
      fontWeight: '500',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    filterBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 8px',
      fontSize: '1rem',
      fontWeight: '500',
      borderRadius: '9999px'
    }
  };

  return (
    <div 
      style={styles.resourceCard}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={styles.cardContent}>
        <div style={styles.cardHeader}>
          <FileIcon type={resource.type} />
          <div style={styles.cardInfo}>
            <h3 style={styles.cardTitle}>
              {extractResourceTitle(resource.filename)}
            </h3>
            <div style={styles.cardBadges}>
              <span style={{
                ...styles.filterBadge,
                backgroundColor: typeColor.bg,
                color: typeColor.text
              }}>
                {resource.type[0]?.toUpperCase() + resource.type.slice(1)}
              </span>
              <span style={{
                ...styles.filterBadge,
                backgroundColor: '#f3f4f6',
                color: '#374151'
              }}>
                {getFileExtension(resource.filename)}
              </span>
            </div>
            <p className="p-style" style={styles.cardSeries}>
              Series: {resource.series[0]?.toUpperCase() + resource.series.slice(1)}
            </p>
          </div>
        </div>
      </div>
      
      <div style={styles.cardActions}>
        <button
          onClick={() => onOpenFile(resource.url)}
          style={styles.openButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#1d4ed8';
            e.currentTarget.style.color = '#e0e0e0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#83FFE7';
            e.currentTarget.style.color = '#545454';
          }}
        >
          <ExternalLink size={16} style={{ marginRight: '8px' }} />
          Open File
        </button>
      </div>
    </div>
  );
};

export default ResourceCard;